import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { NotificationBell } from '@/components/NotificationBell';
import { NotificationItem } from '@/components/NotificationItem';
import { useInAppNotifications } from '@/hooks/useInAppNotifications';
import { CheckCheck, Loader2, Inbox } from 'lucide-react';

export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { 
    notifications, 
    unreadCount, 
    isLoading, 
    markAsRead, 
    markAllAsRead,
    deleteNotification 
  } = useInAppNotifications();

  const handleNotificationClick = (notification: typeof notifications[0]) => {
    if (notification.reference_id) {
      switch (notification.type) {
        case 'group_message':
          navigate(`/groups/${notification.reference_id}`);
          break;
        case 'challenge':
          navigate('/challenges');
          break;
        case 'achievement':
          navigate('/profile');
          break;
        case 'goal_reached':
          navigate('/discipline-goals');
          break;
        case 'shared_simulation':
          navigate(`/simulados/compartilhado/${notification.reference_id}`);
          break;
        default:
          break;
      }
    }
    setOpen(false);
  };

  const unreadNotifications = notifications.filter(n => !n.read);
  const readNotifications = notifications.filter(n => n.read);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <div>
          <NotificationBell unreadCount={unreadCount} onClick={() => setOpen(true)} />
        </div>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md p-0">
        <SheetHeader className="p-4 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle>Notificações</SheetTitle>
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs"
                onClick={() => markAllAsRead()}
              >
                <CheckCheck className="h-4 w-4 mr-1" />
                Marcar todas como lidas
              </Button>
            )}
          </div>
        </SheetHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Inbox className="h-12 w-12 text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground text-center">
              Nenhuma notificação ainda
            </p>
          </div>
        ) : (
          <Tabs defaultValue="unread" className="flex-1">
            <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0">
              <TabsTrigger 
                value="unread" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
              >
                Não lidas ({unreadCount})
              </TabsTrigger>
              <TabsTrigger 
                value="all" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
              >
                Todas ({notifications.length})
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="h-[calc(100vh-140px)]">
              <TabsContent value="unread" className="m-0 p-2">
                {unreadNotifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <CheckCheck className="h-8 w-8 text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Todas as notificações foram lidas
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {unreadNotifications.map(notification => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onMarkAsRead={markAsRead}
                        onDelete={deleteNotification}
                        onClick={() => handleNotificationClick(notification)}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="all" className="m-0 p-2">
                <div className="space-y-1">
                  {notifications.map(notification => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onMarkAsRead={markAsRead}
                      onDelete={deleteNotification}
                      onClick={() => handleNotificationClick(notification)}
                    />
                  ))}
                </div>
              </TabsContent>
            </ScrollArea>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
}
